import {
  SummaryDetails,
  SummarySectionData
} from 'components/FlowSummarySection/FlowSummarySection';
import { StateDataContext } from 'containers/Common/StateDataContext';
import { useContext, useMemo } from 'react';
import i18n from 'i18n';
import { compact } from 'lodash';
import { ScheduleConfig } from 'core/rtbCampaign/ScheduleData';
import {
  ColumnDefinition,
  getComponentColumn,
  getNameIdColumn
} from 'components/TableColumn/TableColumn';

export type ScheduleSummaryStateContentModel = {
  scheduleSummaryData?: SummarySectionData;
  conflictTableData?: {
    data: {
      id: number;
      name: string;
      scheduleSummaryData: SummarySectionData;
    }[];
    columns: ColumnDefinition[];
  };
};

export const useScheduleSummaryStateContentModel = (
  scheduleConfig: ScheduleConfig,
  conflictIds: number[]
): ScheduleSummaryStateContentModel => {
  const { data } = useContext(StateDataContext);

  const scheduleSummaryData = useMemo(() => {
    if (!data) {
      return;
    }

    const { enableStartDate, startDate, enableEndDate, endDate } = data;

    const scheduleContent = compact([
      enableStartDate
        ? {
            label: i18n.t('campaignSummary.labels.startDate'),
            value: startDate
          }
        : undefined,
      enableEndDate
        ? {
            label: i18n.t('campaignSummary.labels.endDate'),
            value: endDate
          }
        : undefined
    ]);

    return {
      schedule: {
        title: i18n.t('campaignSummary.titles.generalInformation'),
        content: scheduleContent
      }
    };
  }, [data]);

  const conflictTableData = useMemo(() => {
    if (!scheduleConfig.campaigns || scheduleConfig.campaigns.length === 0) {
      return;
    }
    const tableData: {
      id: number;
      name: string;
      scheduleSummaryData: SummarySectionData;
    }[] = scheduleConfig.campaigns
      .filter((campaign) => conflictIds.includes(campaign.id))
      .map((campaign) => ({
        id: campaign.id,
        name: campaign.name,
        scheduleSummaryData: {
          schedule: {
            title: i18n.t('campaignSummary.titles.generalInformation'),
            content: [
              {
                label: i18n.t('campaignSummary.labels.schedule'),
                value: `${campaign.startDate} ~ ${campaign.endDate}`
              }
            ]
          }
        }
      }));
    const columns: ColumnDefinition[] = [
      getNameIdColumn({
        sort: false,
        text: i18n.t('ignoreCampaignSummary.headers.name'),
        dataField: 'id'
      }),
      getComponentColumn(
        {
          sort: false,
          text: i18n.t('ignoreCampaignSummary.headers.originalSettings'),
          dataField: 'scheduleSummaryData'
        },
        SummaryDetails,
        (data) => ({
          summaryData: data.scheduleSummaryData
        })
      )
    ];
    return {
      data: tableData,
      columns
    };
  }, [scheduleConfig.campaigns, conflictIds]);

  return {
    scheduleSummaryData,
    conflictTableData
  };
};
